import { LinearProgress, LinearProgressProps } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import AvatarEditor from "react-avatar-editor";
import Dropzone, { FileError, useDropzone } from "react-dropzone";
import { Icon } from "@iconify/react";
import { cloneDeep } from "lodash";
import { bulkUploadGrantLetters, getGrantTemplate } from "../../api/Esop";
import FileInput from "../../components/shared/FileInput";
import { Label } from "../../components/shared/InputField";
import Tooltip from "../../components/shared/Tooltip";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../components/utils";
import { useAddGrantExcel, useGrants } from "../../queries";
import { useCompanyStore } from "../../store";
import { ValidationObj } from "../../types/Grant";

import SearchDropDown from "../../components/shared/SearchDropdown";
import convertToBase64 from "../../utils/convertToBase64";
import BCHeader from "../../shared/BCHeader";

function UploadGrantLetters() {
  const [statusIndicatorData, setStatusIndicatorData] =
    useState<LinearProgressProps>({ variant: "determinate", value: 0 });
  const { isPlaceholderData, isFetching, data: grantData } = useGrants();
  const fileSizeLimit = { single: 10 * 1024 * 1024, total: 50 * 1024 * 1024 };
  const navigate = useNavigate();
  const [uploadInProgress, setUploadInProgress] = useState(false);
  let totalFileSize = 0;
  const dropzone = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 50,
    maxSize: fileSizeLimit.single,
    validator: (file: File) => {
      const errors: FileError[] = [];
      totalFileSize += file.size;
      if (totalFileSize > fileSizeLimit.total) {
        errors.push({
          message: "Total file size exceeds 50 MB",
          code: "file-too-large",
        });
      }
      if (file.size > fileSizeLimit.single) {
        errors.push({
          message: `File size exceeds ${fileSizeLimit.single / 1024 / 1024} MB`,
          code: "file-too-large",
        });
      }
      return errors.length > 0 ? errors : null;
    },
  });
  const { getRootProps, getInputProps, fileRejections } = dropzone;
  let { acceptedFiles } = dropzone;
  const [grantFilesMap, setGrantFilesMap] = useState<
    Map<
      string,
      {
        file: File | undefined;
        status: string;
        grantIdentifier: string;
        error?: string;
      }
    >
  >(new Map());
  const grantIdentifiers = useMemo(
    () => grantData?.grants?.map((grant) => grant.grantIdentifier) || [],
    [grantData]
  );
  function handleFileUpload() {
    if (grantFilesMap.size === 0) {
      const allowedFileExtensions = ["pdf"];
      const grantFilesMapCopy = cloneDeep(grantFilesMap);
      const grantMap = new Map(grantIdentifiers.map((id) => [id, true]));
      acceptedFiles.forEach((file: any) => {
        const fileName = file.name;
        const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
        grantFilesMapCopy?.set(file.name, {
          file,
          status: "Pending",
          grantIdentifier: "",
        });
        if (
          allowedFileExtensions.some((extension) =>
            file.name.endsWith(extension)
          )
        ) {
          let grantIdentifier: string | undefined;
          if (grantMap.has(fileNameWithoutExt)) {
            grantIdentifier = fileNameWithoutExt;
          } else {
            grantIdentifier = grantIdentifiers.find((id) =>
              fileNameWithoutExt.includes(id)
            );
          }
          if (grantIdentifier) {
            grantFilesMapCopy?.set(file.name, {
              file,
              status: "Pending",
              grantIdentifier,
            });
          }
        }
      });
      setGrantFilesMap(grantFilesMapCopy);
      toast("Files have been matched with respective grants wherever found", {
        autoClose: 2000,
        type: "info",
      });
    } else {
      const isValid = !validateImports();
      if (!isValid) {
        toast("Found errors in mapping, please correct to continue", {
          type: "error",
          autoClose: 2000,
        });
      } else {
        makeChunksAndUpload();
      }
    }
  }
  async function makeChunksAndUpload() {
    const chunkSize = 10;
    const entriesArray = Array.from(grantFilesMap.entries());
    const grantFilesMapCopy = cloneDeep(grantFilesMap);
    grantFilesMapCopy.forEach((grant) => {
      grant.status = "uploading";
    });
    let error = "";
    setGrantFilesMap(grantFilesMapCopy);
    for (let i = 0; i < entriesArray.length; i += chunkSize) {
      const chunk = entriesArray.slice(i, i + chunkSize);
      /* eslint-disable no-await-in-loop */
      const uploadStatus = await uploadGrantLetters(chunk);
      if (uploadStatus !== "") error = uploadStatus;
    }
    if (!error) {
      toast("Grant letters have been uploaded successfully", {
        autoClose: 2000,
        type: "success",
      });
      setTimeout(() => {
        navigate("/options/allPlans");
      }, 1000);
    }
  }
  async function convertFileToBase64(grantId: string, file: File) {
    const base64File: string = (await convertToBase64(file)) as string;
    return { grantId, base64File };
  }
  async function uploadGrantLetters(
    chunk: [
      string,
      {
        file: File | undefined;
        status: string;
        grantIdentifier: string;
        error?: string;
      }
    ][]
  ) {
    const chunkedArray = Array.from(chunk);
    const convertedList = await Promise.all(
      chunkedArray.map((chunkedData) => {
        if (chunkedData[1].file)
          return convertFileToBase64(
            chunkedData[1].grantIdentifier,
            chunkedData[1].file
          );
        return { grantId: chunkedData[1].grantIdentifier, base64File: "" };
      })
    );
    let error = "";
    try {
      const uploaded = await bulkUploadGrantLetters(convertedList);
    } catch (e) {
      toast("Error while uploading some files", {
        autoClose: 2000,
        type: "error",
      });
      error = "Batch upload Failed";
    }
    const grantFilesMapCopy = cloneDeep(grantFilesMap);
    const chunkedGrant = chunk.map(
      (chunkedData) => chunkedData[1].grantIdentifier
    );
    grantFilesMapCopy.forEach((grant) => {
      if (chunkedGrant.includes(grant.grantIdentifier)) {
        grant.status = "success";
        if (error) {
          grant.status = "error";
          grant.error = error;
        }
      }
    });
    setGrantFilesMap(grantFilesMapCopy);
    return error;
  }
  function validateImports() {
    const grantFilesMapCopy = cloneDeep(grantFilesMap);
    let errorsExist = false;
    grantFilesMapCopy.forEach((grantFile) => {
      let error = "";
      if (!grantFile.grantIdentifier) error = "No identifier found";
      else if (
        Array.from(grantFilesMapCopy.values()).filter(
          (value) => value.grantIdentifier === grantFile.grantIdentifier
        ).length > 1
      )
        error = "Duplicate identifier found";
      if (error) {
        grantFile.error = error;
        errorsExist = true;
      }
    });
    setGrantFilesMap(grantFilesMapCopy);
    return errorsExist;
  }
  function handleGrantMapChange(fileKey: string, value: string) {
    const grantFilesMapCopy = cloneDeep(grantFilesMap);
    const existingConfig = grantFilesMap.get(fileKey);
    grantFilesMapCopy.set(fileKey, {
      file: existingConfig?.file,
      grantIdentifier: value,
      status: existingConfig?.status || "Pending",
    });
    setGrantFilesMap(grantFilesMapCopy);
  }
  const clearAllGrantAssociations = () => {
    const grantFilesMapCopy = cloneDeep(grantFilesMap);
    grantFilesMapCopy.forEach((grantFile) => {
      grantFile.grantIdentifier = "";
      grantFile.status = "Pending";
      grantFile.error = undefined;
    });
    setGrantFilesMap(grantFilesMapCopy);
    toast("All associations cleared", {
      autoClose: 2000,
      type: "info",
    });
  };
  const clearAllFiles = () => {
    acceptedFiles = [];
    setUploadInProgress(false);
    toast("All files cleared", {
      autoClose: 2000,
      type: "info",
    });
  };
  function onClear() {
    if (grantFilesMap.size > 0) {
      clearAllGrantAssociations();
    } else {
      clearAllFiles();
    }
  }

  function onDiscard() {
    setGrantFilesMap(new Map());
    setUploadInProgress(false);
    toast("Upload discarded", {
      autoClose: 2000,
      type: "info",
    });
  }

  return (
    <VStack>
      <BCHeader
        className="items-baseline py-3"
        bcTitle="Plans & Grants"
        bcSubTitle="Upload Grant Letters"
        bcDescription="Upload grant letters to associate them with the respective grants in the system."
      ></BCHeader>
      <VStack className="justify-between w-full h-full px-4 bg-white border-2 rounded-md min-h-[600px]">
        <VStack className="overflow-auto">
          <HStack className="py-4 text-lg font-medium text-left bg-white border-b">
            <h6 className="flex-1">Upload Grant Letters</h6>
          </HStack>
          {grantFilesMap?.size === 0 && (
            <Box className="mx-auto h-96 w-3/4 bg-gray-50 pt-8 mt-6 cursor-copy">
              <div className="flex w-full h-full justify-center">
                <div
                  {...getRootProps({
                    className: "dropzone  align-middle flex flex-col gap-4",
                  })}
                >
                  <HStack>
                    <Icon
                      width={64}
                      height={64}
                      className="text-red-500 text-center flex-row w-full"
                      icon={"mdi:folder-upload"}
                    ></Icon>
                  </HStack>
                  <HStack className="text-center justify-center pb-4">
                    <input {...getInputProps()} />
                    <p>
                      Drag drop grant letters here, or click to select them.
                    </p>
                  </HStack>
                  <VStack className="text-xxs justify-start text-start">
                    <li>
                      If the grant letters are named as per their grant
                      identifier, the system will automatically match the grant
                      identifier to the grants in the system.
                    </li>
                    <li>
                      If the grant letters names do not match the grant
                      identifier, you will be prompted to match the grant
                      identifier to the grants in the system.
                    </li>
                    <li>A maximum of 50 files can be uploaded at once.</li>
                    <li>
                      Each file must be a PDF and should not exceed 10 MB in
                      size.
                    </li>

                    <li>
                      A new upload will clear the files previously uploaded.
                    </li>
                    {acceptedFiles.length > 0 && (
                      <p className="mt-4">
                        <span className="text-xl">{acceptedFiles.length}</span>{" "}
                        files uploaded, click on continue to begin the import
                        process
                      </p>
                    )}
                    {fileRejections.length > 0 && (
                      <p className="text-red-500 text-xl">
                        {fileRejections.length} files rejected.
                      </p>
                    )}
                    {fileRejections.length > 0 && (
                      <ul className="text-red-500 max-h-20 overflow-y-auto p-4">
                        {fileRejections.map((file) => (
                          <li key={file.file.name}>
                            {file.file.name} - {file.errors[0].message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </VStack>
                </div>
              </div>
            </Box>
          )}
          {grantFilesMap?.size > 0 && (
            <Box className="mx-auto h-96 w-3/4">
              <VStack className="w-full h-96 justify-center">
                <HStack className="min-h-16 justify-center align-middle">
                  <table className="w-full flex-1">
                    <thead>
                      <tr>
                        <td>File name</td>
                        <td>Grant Identifier</td>
                      </tr>
                    </thead>
                    <tbody className="">
                      {Array.from(grantFilesMap).map((filename, idex) => (
                        <tr
                          key={idex}
                          className="min-h-24 justify-center align-middle border-b-2 border-dotted"
                        >
                          <td className="h-20">{filename[0]}</td>
                          <td className="">
                            <SearchDropDown
                              onChange={(e, value) => {
                                handleGrantMapChange(filename[0], value);
                              }}
                              key={`grantInfo-${idex}`}
                              placeholder="-- Select Field --"
                              options={grantIdentifiers}
                              value={filename[1].grantIdentifier}
                              className={""}
                              getOptionLabel={(option) => `${option}`}
                              isOptionEqualToValue={(option, value) =>
                                (option || "") === (value || "")
                              }
                            />
                            {filename[1]?.error && (
                              <span className="text-red-400 text-xxs">
                                {filename[1]?.error}
                              </span>
                            )}
                            {filename[1]?.status === "success" && (
                              <span className="text-green-400 text-xxs">
                                File uploaded
                              </span>
                            )}
                            {filename[1]?.status === "uploading" && (
                              <span className="w-full rounded-lg align-middle justify-center mx-auto inline-flex">
                                <LinearProgress
                                  variant="indeterminate"
                                  className="w-full pt-2 rounded-lg"
                                  color="info"
                                />
                              </span>
                            )}
                            {uploadInProgress &&
                              filename[1]?.status === "Pending" && (
                                <span className="text-blue-400">
                                  Queued for upload
                                </span>
                              )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </HStack>
              </VStack>
            </Box>
          )}
        </VStack>
        <HStack className="p-4 justify-end gap-4">
          {grantFilesMap.size > 0 && (
            <>
              <ButtonSecondary
                onClick={() => {
                  onDiscard();
                }}
              >
                Discard
              </ButtonSecondary>
              <ButtonSecondary
                onClick={() => {
                  onClear();
                }}
              >
                Clear
              </ButtonSecondary>
            </>
          )}
          <ButtonPrimary
            disabled={acceptedFiles.length === 0}
            onClick={() => handleFileUpload()}
          >
            Continue
          </ButtonPrimary>
        </HStack>
      </VStack>
    </VStack>
  );
}

export default UploadGrantLetters;
